import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import OnboardingShell from './OnboardingShell';
import { loadOnboardingDraft, sampleCaption, saveOnboardingStep } from './samples';

export default function PreviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const draft = loadOnboardingDraft(user?.id);
  const [generating, setGenerating] = useState(true);
  const [content, setContent] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    saveOnboardingStep('/onboarding/preview', user?.id);
  }, [user?.id]);

  useEffect(() => {
    if (!draft.tone || !draft.topic) return;
    let cancelled = false;
    setGenerating(true);
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      setContent(sampleCaption(draft.tone, draft.topic) || t('onboarding.preview.fallback'));
      setGenerating(false);
    }, 2000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [draft.tone, draft.topic, tick, t]);

  if (!draft.tone || !draft.topic) {
    return <Navigate to="/onboarding/tone" replace />;
  }

  const toneNames: Record<string, string> = {
    profesyonel: t('onboarding.tones.profesyonel').replace(/^👔\s*/, ''),
    samimi: t('onboarding.tones.samimi').replace(/^😊\s*/, ''),
    espritli: t('onboarding.tones.espritli').replace(/^✨\s*/, ''),
    egitici: t('onboarding.tones.egitici').replace(/^📚\s*/, ''),
  };
  const toneLabel = toneNames[draft.tone] || draft.tone;
  const topicLabel = t(`onboarding.topics.${draft.topic}`);

  return (
    <OnboardingShell title={t('onboarding.preview.title')}>
      <div className="mb-10">
        <div className="mb-3 text-xs font-semibold text-[#b892ff]">{t('onboarding.stepOf', { current: 2, total: 4 })}</div>
        <h2 className="m-0 text-[36px] font-extrabold tracking-[-0.02em]">
          {generating ? t('onboarding.preview.titleGenerating') : t('onboarding.preview.titleReady')}
        </h2>
      </div>

      {generating ? (
        <div className="mb-8 rounded-xl border border-[#2b1f33] bg-[#221733] p-10 text-center">
          <div className="mb-5 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#3d2e46] border-t-[#ff6f91]" />
          </div>
          <p className="m-0 text-base text-[#c9b8d1]">
            {t('onboarding.preview.generating', { tone: toneLabel, topic: topicLabel })}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-8 rounded-xl border border-[#ff6f91] bg-[#3a1a2a] p-6">
            <div className="mb-3 text-[13px] font-semibold text-[#ff6f91]">{t('onboarding.preview.sampleLabel')}</div>
            <div className="mb-4 rounded-lg bg-[#221733] p-4 text-[15px] leading-relaxed text-[#f5eef7]">{content}</div>
            <div className="flex gap-2 text-xs text-[#a8899f]">
              <span>{t('onboarding.preview.fakeLikes')}</span>
              <span>{t('onboarding.preview.fakeComments')}</span>
              <span>{t('onboarding.preview.fakeShares')}</span>
            </div>
          </div>

          <div className="mb-8 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setTick((n) => n + 1)}
              className="rounded-lg border border-[#3d2e46] bg-transparent py-3 text-sm font-semibold text-[#f5eef7]"
            >
              {t('onboarding.preview.regenerate')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/onboarding/connect')}
              className="rounded-lg border-none bg-[#ff6f91] py-3 text-sm font-semibold text-[#2b0e18] hover:bg-[#ff85a3]"
            >
              {t('onboarding.preview.approve')}
            </button>
          </div>
        </>
      )}

      <Link
        to="/onboarding/tone"
        className="block rounded-lg border border-[#3d2e46] bg-transparent py-3 text-center text-sm font-semibold text-[#f5eef7]"
      >
        {t('onboarding.back')}
      </Link>
    </OnboardingShell>
  );
}
